import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import { useAuth } from '../../contexts/AuthContext';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const RECAPTCHA_ENABLED = RECAPTCHA_SITE_KEY && RECAPTCHA_SITE_KEY.trim() !== '';

/**
 * Email + OTP sign-in for donate checkout without leaving the page (same API flow as LoginPage).
 */
export default function DonateCheckoutSignInDialog({ open, onClose }) {
  const { requestOtp, verifyOtpLocal } = useAuth();
  const [email, setEmail] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [otpLoading, setOtpLoading] = React.useState(false);
  const [resending, setResending] = React.useState(false);
  const [code, setCode] = React.useState('');
  const [expiresIn, setExpiresIn] = React.useState(300);
  const [awaitingOtp, setAwaitingOtp] = React.useState(false);
  const [error, setError] = React.useState('');
  const [recaptchaToken, setRecaptchaToken] = React.useState(null);
  const recaptchaRef = React.useRef(null);

  const resetForm = React.useCallback(() => {
    setEmail('');
    setCode('');
    setError('');
    setAwaitingOtp(false);
    setExpiresIn(300);
    setRecaptchaToken(null);
    try {
      recaptchaRef.current?.reset();
    } catch {
      /* ignore */
    }
  }, []);

  React.useEffect(() => {
    if (!open) resetForm();
  }, [open, resetForm]);

  React.useEffect(() => {
    if (!awaitingOtp) return undefined;
    const timer = window.setInterval(() => {
      setExpiresIn((s) => Math.max(0, s - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [awaitingOtp]);

  React.useEffect(() => {
    if (awaitingOtp && expiresIn === 0) {
      setAwaitingOtp(false);
      setCode('');
      setError('OTP expired. Please request a new code.');
    }
  }, [awaitingOtp, expiresIn]);

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    setError('');
  };

  const onSubmitEmail = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !email.trim()) {
      setError('Email is required');
      return;
    }
    if (RECAPTCHA_ENABLED && !recaptchaToken) {
      setError('Please complete the reCAPTCHA verification');
      return;
    }
    try {
      setLoading(true);
      await requestOtp(email.trim(), recaptchaToken);
      try {
        recaptchaRef.current?.reset();
      } catch {
        /* ignore */
      }
      setRecaptchaToken(null);
      setAwaitingOtp(true);
      setExpiresIn(300);
      setCode('');
    } catch (err) {
      setError(err?.message || 'Failed to send OTP');
      try {
        recaptchaRef.current?.reset();
      } catch {
        /* ignore */
      }
      setRecaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  const onVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Email is required');
      setAwaitingOtp(false);
      return;
    }
    if (!code.trim()) {
      setError('OTP code is required');
      return;
    }
    try {
      setOtpLoading(true);
      await verifyOtpLocal({ email: email.trim(), userInputCode: code.trim() });
      resetForm();
      onClose();
    } catch (err) {
      setError(err?.message || 'Invalid OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const onResendOtp = async () => {
    setError('');
    if (!email.trim()) {
      setAwaitingOtp(false);
      setError('Email is required');
      return;
    }
    try {
      setResending(true);
      await requestOtp(email.trim());
      setExpiresIn(300);
      setCode('');
    } catch (err) {
      setError(err?.message || 'Failed to resend OTP');
    } finally {
      setResending(false);
    }
  };

  const onBackToEmail = () => {
    setAwaitingOtp(false);
    setCode('');
    setError('');
    setExpiresIn(300);
  };

  const handleDialogClose = () => {
    if (loading || otpLoading || resending) return;
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={{ fontWeight: 800 }}>{awaitingOtp ? 'Enter OTP' : 'Sign in to continue'}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Use the same email as your devotee portal account. We will send a one-time code to sign you in without
          leaving checkout.
        </Typography>
        {awaitingOtp ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Sent to: {email || 'your email'} • Expires in {Math.floor(expiresIn / 60)}:
              {String(expiresIn % 60).padStart(2, '0')}
            </Typography>
            <Box component="form" onSubmit={onVerifyOtp} id="donate-signin-otp-form">
              <TextField
                fullWidth
                label="OTP Code"
                value={code}
                onChange={(e) => setCode(e.target.value.trim())}
                margin="normal"
                autoComplete="one-time-code"
                autoFocus
              />
              {error ? (
                <Alert severity="error" sx={{ mt: 1.5 }}>
                  {error}
                </Alert>
              ) : null}
            </Box>
          </>
        ) : (
          <Box component="form" onSubmit={onSubmitEmail} id="donate-signin-email-form">
            <TextField
              type="email"
              fullWidth
              label="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              helperText="Use the same email you use for ICCT donations."
              autoComplete="email"
              required
            />
            {error ? (
              <Alert severity="error" sx={{ mt: 1.5 }}>
                {error}
              </Alert>
            ) : null}
            {RECAPTCHA_ENABLED ? (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={handleRecaptchaChange}
                  onExpired={() => setRecaptchaToken(null)}
                  onError={() => {
                    setRecaptchaToken(null);
                    setError('reCAPTCHA error. Please try again.');
                  }}
                />
              </Box>
            ) : null}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
        {awaitingOtp ? (
          <>
            <Button variant="outlined" onClick={handleDialogClose} disabled={otpLoading}>
              Cancel
            </Button>
            <Button variant="text" onClick={onBackToEmail} disabled={otpLoading}>
              Change email
            </Button>
            <Box sx={{ flex: '1 1 auto' }} />
            <Button variant="outlined" onClick={onResendOtp} disabled={resending || otpLoading}>
              {resending ? 'Resending…' : 'Resend OTP'}
            </Button>
            <Button variant="contained" type="submit" form="donate-signin-otp-form" disabled={otpLoading || !code.trim()}>
              {otpLoading ? 'Verifying…' : 'Verify'}
            </Button>
          </>
        ) : (
          <>
            <Button variant="outlined" onClick={handleDialogClose} disabled={loading}>
              Cancel
            </Button>
            <Box sx={{ flex: '1 1 auto' }} />
            <Button
              variant="contained"
              type="submit"
              form="donate-signin-email-form"
              disabled={loading || !email.trim() || (RECAPTCHA_ENABLED && !recaptchaToken)}
            >
              {loading ? 'Sending…' : 'Send OTP'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
